import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTOR_TYPES = [
  { value: "agencia", label: "Agência de marketing político" },
  { value: "consultor", label: "Consultor / estrategista independente" },
];

const INITIAL = {
  actor_type: "",
  name: "",
  contact_name: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  website: "",
  clients_profile: "",
  message: "",
};

export default function AgencyLeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.actor_type || !form.name || !form.email) {
      toast.error("Preencha tipo de atuação, nome e e-mail.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/agency-leads`, {
        ...form,
        contact_name: form.contact_name || null,
        phone: form.phone || null,
        city: form.city || null,
        state: form.state || null,
        website: form.website || null,
        clients_profile: form.clients_profile || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Cadastro recebido. Vamos retornar em até 5 dias úteis.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Não foi possível enviar agora. Tente novamente em instantes.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="parceria"
      data-testid="agency-form-section"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden"
    >
      <div
        className="absolute -top-32 -right-20 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #7E20FF, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 -left-20 h-[400px] w-[400px] rounded-full blur-3xl opacity-20"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Programa de Parceiros
          </span>
          <h2
            data-testid="agency-form-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.96] text-[#F5FAFB]"
          >
            Quero ser parceiro RSM Politics.
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F5FAFB]/70 max-w-md">
            Conte um pouco sobre o seu trabalho. Vamos responder em até 5 dias
            úteis — conversa de alinhamento, sem compromisso.
          </p>

          <div className="mt-10 space-y-4 text-sm text-[#F5FAFB]/75 max-w-sm">
            {[
              "Suporte direto do time de Brasília.",
              "Acesso a ambiente de demonstração da plataforma.",
              "Programa formal em construção — entre na primeira leva.",
            ].map((t, i) => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#3ED383] flex-shrink-0"
                />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="agency-form-success"
              className="rounded-2xl border border-[#3ED383]/30 bg-white/5 backdrop-blur p-10 text-center"
            >
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#3ED383]/20 text-[#3ED383]">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-[#F5FAFB]">
                Cadastro recebido.
              </h3>
              <p className="mt-3 text-[#F5FAFB]/70 max-w-md mx-auto">
                Em até 5 dias úteis, alguém da RSM Politics retorna para uma
                conversa de alinhamento sobre o programa de parceiros.
              </p>
              <button
                data-testid="agency-form-reset"
                onClick={() => setDone(false)}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-[#F5FAFB]/85 hover:border-[#7E20FF] hover:text-[#7E20FF] transition-colors"
              >
                Cadastrar outro contato
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="agency-form"
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Tipo de atuação *">
                  <select
                    data-testid="agency-input-actor-type"
                    required
                    value={form.actor_type}
                    onChange={set("actor_type")}
                    className={inputCls}
                  >
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {ACTOR_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={form.actor_type === "consultor" ? "Seu nome *" : "Nome da agência *"}>
                  <input
                    data-testid="agency-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder={form.actor_type === "consultor" ? "Nome completo" : "Razão social ou marca"}
                    className={inputCls}
                  />
                </Field>
                {form.actor_type === "agencia" && (
                  <Field label="Pessoa de contato">
                    <input
                      data-testid="agency-input-contact-name"
                      value={form.contact_name}
                      onChange={set("contact_name")}
                      placeholder="Quem responde por essa conversa"
                      className={inputCls}
                    />
                  </Field>
                )}
                <Field label="E-mail *">
                  <input
                    data-testid="agency-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="contato@agencia.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Telefone / WhatsApp">
                  <input
                    data-testid="agency-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(11) 90000-0000"
                    className={inputCls}
                  />
                </Field>
                <Field label="Cidade">
                  <input
                    data-testid="agency-input-city"
                    value={form.city}
                    onChange={set("city")}
                    placeholder="Ex.: São Paulo"
                    className={inputCls}
                  />
                </Field>
                <Field label="Estado">
                  <input
                    data-testid="agency-input-state"
                    value={form.state}
                    onChange={set("state")}
                    placeholder="UF · ex.: SP"
                    className={inputCls}
                  />
                </Field>
                <Field label="Site / portfólio" className="sm:col-span-2">
                  <input
                    data-testid="agency-input-website"
                    value={form.website}
                    onChange={set("website")}
                    placeholder="https://"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="Perfil dos clientes / atuação" className="mt-4">
                <textarea
                  data-testid="agency-input-clients-profile"
                  value={form.clients_profile}
                  onChange={set("clients_profile")}
                  rows={3}
                  placeholder="Que campanhas vocês atendem? Majoritárias, proporcionais, partidos, mandatos…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <Field label="Algo mais que queira contar?" className="mt-4">
                <textarea
                  data-testid="agency-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  placeholder="Contexto, expectativa, prazos…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="agency-form-submit"
                className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#7E20FF] px-7 py-4 text-base font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:bg-[#6818D6] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#7E20FF] focus:ring-offset-2 focus:ring-offset-[#1B1D1D]"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    Quero ser parceiro RSM Politics
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-[#F5FAFB]/55">
                Conversa sem compromisso. Seus dados não são compartilhados e
                servem apenas para a articulação do programa de parceiros.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#F5FAFB]/55">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#0e0f0f] px-4 py-3 text-sm text-[#F5FAFB] placeholder:text-[#F5FAFB]/30 focus:border-[#7E20FF] focus:outline-none focus:ring-2 focus:ring-[#7E20FF]/30 transition-all";
